import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, X, Eye, Building2, Car, Monitor, Sofa,
  Wrench, DollarSign, Calendar, MapPin, User, FileText, Upload,
  TrendingDown, Package, CreditCard, Filter, Download,
  BookOpen, Wallet
} from 'lucide-react';
import { useFocusEntity } from '../hooks/useFocusEntity';

const Assets = () => {
  const navigate = useNavigate();
  const { focusedId } = useFocusEntity();
  const [assets, setAssets] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Phase 7.x · Fix #5 · Batch 1 — financial-graph context for the
  // purchase journal (asset GL + payment-source account pickers).
  const [accounts, setAccounts] = useState([]);
  const [pettyFunds, setPettyFunds] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  const [formData, setFormData] = useState({
    name_arabic: '',
    name_english: '',
    asset_type: 'equipment',
    category: 'fixed',
    description: '',
    purchase_date: new Date().toISOString().split('T')[0],
    purchase_value: '',
    useful_life_years: '',
    salvage_value: '',
    location: '',
    project_id: '',
    employee_id: '',
    serial_number: '',
    warranty_expiry: '',
    notes: '',
    // Phase 7.x · Fix #5 — purchase journal contract fields.
    linked_account_id: '',
    payment_source: 'payable',
    payable_account_id: '',
    petty_cash_fund_id: '',
    bank_account_id: '',
    cash_account_id: ''
  });

  const assetTypes = {
    // Fixed Assets
    building: { label: 'Building / مبنى', icon: Building2, color: 'blue' },
    land: { label: 'Land / أرض', icon: MapPin, color: 'green' },
    vehicle: { label: 'Vehicle / مركبة', icon: Car, color: 'orange' },
    equipment: { label: 'Equipment / معدات', icon: Wrench, color: 'purple' },
    furniture: { label: 'Furniture / أثاث', icon: Sofa, color: 'pink' },
    computer: { label: 'Computer / حاسوب', icon: Monitor, color: 'cyan' },
    machinery: { label: 'Machinery / آلات', icon: Wrench, color: 'red' },
    // Current Assets
    cash: { label: 'Cash / نقدية', icon: DollarSign, color: 'green' },
    inventory: { label: 'Inventory / مخزون', icon: Package, color: 'yellow' },
    receivables: { label: 'Receivables / ذمم مدينة', icon: CreditCard, color: 'blue' },
    prepaid: { label: 'Prepaid / مصاريف مقدمة', icon: Calendar, color: 'purple' },
    other: { label: 'Other / أخرى', icon: FileText, color: 'gray' }
  };

  const statusColors = {
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-gray-100 text-gray-700',
    sold: 'bg-blue-100 text-blue-700',
    disposed: 'bg-red-100 text-red-700',
    under_maintenance: 'bg-yellow-100 text-yellow-700'
  };

  const statusLabels = {
    active: 'Active / نشط',
    inactive: 'Inactive / معطل',
    sold: 'Sold / مباع',
    disposed: 'Disposed / متخلص منه',
    under_maintenance: 'Under Maintenance / تحت الصيانة'
  };

  useEffect(() => {
    fetchAssets();
    fetchSummary();
    fetchProjects();
    fetchEmployees();
    fetchFinancialContext();
  }, [filterCategory, filterStatus]);

  // Phase 7.x · Fix #5 — populate selectors for the purchase journal.
  const fetchFinancialContext = async () => {
    try {
      const [acc, pcf, bnk] = await Promise.all([
        axiosInstance.get('/accounts'),
        axiosInstance.get('/petty-cash/funds'),
        axiosInstance.get('/bank/accounts')
      ]);
      setAccounts(acc.data || []);
      setPettyFunds((pcf.data || []).filter(f => f.status === 'active'));
      setBankAccounts(bnk.data || []);
    } catch (error) {
      // non-fatal — purchase journal validation will catch missing GL
      console.warn('Failed to fetch financial context');
    }
  };

  const fetchAssets = async () => {
    try {
      let url = '/assets';
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterStatus) params.append('status', filterStatus);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axiosInstance.get(url);
      setAssets(response.data);
      
      // Fetch all assets for counts (without filters)
      const allResponse = await axiosInstance.get('/assets');
      setAllAssets(allResponse.data);
    } catch (error) {
      toast.error('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axiosInstance.get('/assets/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        purchase_value: parseFloat(formData.purchase_value) || 0,
        useful_life_years: formData.category === 'fixed' ? parseInt(formData.useful_life_years) || null : null,
        salvage_value: formData.category === 'fixed' ? parseFloat(formData.salvage_value) || 0 : 0
      };

      if (editingAsset) {
        await axiosInstance.put(`/assets/${editingAsset.id}`, submitData);
        toast.success('Asset updated successfully');
      } else {
        await axiosInstance.post('/assets', submitData);
        toast.success('Asset created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchAssets();
      fetchSummary();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        toast.error(errorDetail.map(e => e.msg).join(', '));
      } else {
        toast.error(errorDetail || 'Operation failed');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    try {
      await axiosInstance.delete(`/assets/${id}`);
      toast.success('Asset deleted successfully');
      fetchAssets();
      fetchSummary();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      name_arabic: asset.name_arabic || '',
      name_english: asset.name_english || '',
      asset_type: asset.asset_type || 'equipment',
      category: asset.category || 'fixed',
      description: asset.description || '',
      purchase_date: asset.purchase_date || '',
      purchase_value: asset.purchase_value || '',
      useful_life_years: asset.useful_life_years || '',
      salvage_value: asset.salvage_value || '',
      location: asset.location || '',
      project_id: asset.project_id || '',
      employee_id: asset.employee_id || '',
      serial_number: asset.serial_number || '',
      warranty_expiry: asset.warranty_expiry || '',
      notes: asset.notes || ''
    });
    setShowModal(true);
  };

  const handleFileUpload = async (e, assetId) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      await axiosInstance.post(`/assets/${assetId}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded successfully');
      fetchAssets();
      if (selectedAsset?.id === assetId) {
        const response = await axiosInstance.get(`/assets/${assetId}`);
        setSelectedAsset(response.data);
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name_arabic: '',
      name_english: '',
      asset_type: 'equipment',
      category: 'fixed',
      description: '',
      purchase_date: new Date().toISOString().split('T')[0],
      purchase_value: '',
      useful_life_years: '',
      salvage_value: '',
      location: '',
      project_id: '',
      employee_id: '',
      serial_number: '',
      warranty_expiry: '',
      notes: '',
      linked_account_id: '',
      payment_source: 'payable',
      payable_account_id: '',
      petty_cash_fund_id: '',
      bank_account_id: '',
      cash_account_id: ''
    });
    setEditingAsset(null);
  };

  // Phase 7.x · Fix #5 — purchase form validation hints.
  const selectedFund = pettyFunds.find(f => f.id === formData.petty_cash_fund_id);
  const purchaseTotal = parseFloat(formData.purchase_value || 0);
  const pcInsufficient =
    formData.payment_source === 'petty_cash' && selectedFund &&
    purchaseTotal > (selectedFund.current_balance || 0);

  const viewAssetDetails = async (asset) => {
    try {
      const response = await axiosInstance.get(`/assets/${asset.id}`);
      setSelectedAsset(response.data);
    } catch (error) {
      toast.error('Failed to fetch asset details');
    }
  };

  const getAssetIcon = (type) => {
    const IconComponent = assetTypes[type]?.icon || FileText;
    return <IconComponent className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="page-title">Assets / الأصول</h2>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary"
          data-testid="new-asset-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Asset
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <p className="text-blue-100 text-sm">Fixed Assets</p>
            <p className="text-2xl font-bold">{summary.total_fixed_assets?.toLocaleString()} SAR</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
            <p className="text-green-100 text-sm">Current Assets</p>
            <p className="text-2xl font-bold">{summary.total_current_assets?.toLocaleString()} SAR</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
            <p className="text-purple-100 text-sm">Total Assets</p>
            <p className="text-2xl font-bold">{summary.total_assets?.toLocaleString()} SAR</p>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
            <p className="text-red-100 text-sm">Total Depreciation</p>
            <p className="text-2xl font-bold">{summary.total_depreciation?.toLocaleString()} SAR</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
            <p className="text-orange-100 text-sm">Book Value</p>
            <p className="text-2xl font-bold">{summary.total_book_value?.toLocaleString()} SAR</p>
          </div>
        </div>
      )}

      {/* Category Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setFilterCategory('')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
            filterCategory === '' 
              ? 'bg-white text-purple-600 shadow-md' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="tab-all-assets"
        >
          <Package className="w-5 h-5" />
          All Assets
          <span className="bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full text-sm">
            {allAssets.length}
          </span>
        </button>
        <button
          onClick={() => setFilterCategory('fixed')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
            filterCategory === 'fixed' 
              ? 'bg-blue-600 text-white shadow-md' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="tab-fixed-assets"
        >
          <Building2 className="w-5 h-5" />
          Fixed Assets / أصول ثابتة
          <span className={`px-2 py-0.5 rounded-full text-sm ${
            filterCategory === 'fixed' ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-600'
          }`}>
            {allAssets.filter(a => a.category === 'fixed').length}
          </span>
        </button>
        <button
          onClick={() => setFilterCategory('current')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
            filterCategory === 'current' 
              ? 'bg-green-600 text-white shadow-md' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="tab-current-assets"
        >
          <DollarSign className="w-5 h-5" />
          Current Assets / أصول متداولة
          <span className={`px-2 py-0.5 rounded-full text-sm ${
            filterCategory === 'current' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-600'
          }`}>
            {allAssets.filter(a => a.category === 'current').length}
          </span>
        </button>
      </div>

      {/* Status Filter */}
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <span className="text-gray-600">Filter by Status:</span>
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="form-input py-2"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="sold">Sold</option>
          <option value="disposed">Disposed</option>
          <option value="under_maintenance">Under Maintenance</option>
        </select>
      </div>

      {/* Assets Table */}
      <div className="table-container">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="table-header">Asset #</th>
                <th className="table-header">Name</th>
                <th className="table-header">Type</th>
                <th className="table-header">Category</th>
                <th className="table-header">Source</th>
                <th className="table-header">Purchase Value</th>
                <th className="table-header">Book Value</th>
                <th className="table-header">Journal</th>
                <th className="table-header">Status</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.length > 0 ? (
                assets.map((asset) => (
                  <tr
                    key={asset.id}
                    className={`table-row ${focusedId === asset.id ? 'focus-highlight' : ''}`}
                    data-testid={`asset-row-${asset.id}`}
                  >
                    <td className="table-cell font-mono text-purple-600">{asset.asset_number}</td>
                    <td className="table-cell">
                      <div>
                        <p className="font-semibold">{asset.name_english}</p>
                        <p className="text-sm text-gray-500">{asset.name_arabic}</p>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        {getAssetIcon(asset.asset_type)}
                        <span>{assetTypes[asset.asset_type]?.label.split('/')[0] || asset.asset_type}</span>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        asset.category === 'fixed' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {asset.category === 'fixed' ? 'Fixed' : 'Current'}
                      </span>
                    </td>
                    <td className="table-cell">
                      {asset.payment_source === 'petty_cash' ? (
                        <span data-testid={`asset-source-petty-${asset.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 text-xs font-medium">
                          <Wallet className="w-3 h-3" />
                          {asset.petty_cash_fund_name || 'Petty Cash'}
                        </span>
                      ) : asset.payment_source === 'bank' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium">
                          <Building2 className="w-3 h-3" />
                          Bank
                        </span>
                      ) : asset.payment_source === 'cash' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium">
                          <DollarSign className="w-3 h-3" />
                          Cash
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium">
                          <CreditCard className="w-3 h-3" />
                          Payable
                        </span>
                      )}
                    </td>
                    <td className="table-cell font-semibold">{asset.purchase_value?.toLocaleString()} SAR</td>
                    <td className="table-cell">
                      <div>
                        <p className="font-semibold">{asset.book_value?.toLocaleString()} SAR</p>
                        {asset.category === 'fixed' && asset.accumulated_depreciation > 0 && (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <TrendingDown className="w-3 h-3" />
                            -{asset.accumulated_depreciation?.toLocaleString()}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      {asset.journal_id ? (
                        <button
                          type="button"
                          data-testid={`open-asset-journal-${asset.id}`}
                          title={`القيد المرتبط · ${asset.journal_id.slice(0, 12)}…`}
                          onClick={() => navigate(`/journal?focus=${asset.journal_id}`)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors text-xs font-medium border border-indigo-200"
                        >
                          <BookOpen className="w-3.5 h-3.5" />
                          افتح القيد
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[asset.status]}`}>
                        {statusLabels[asset.status]?.split('/')[0] || asset.status}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex gap-2">
                        <button
                          onClick={() => viewAssetDetails(asset)}
                          className="btn-icon"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleEdit(asset)}
                          className="btn-icon"
                          title="Edit"
                          data-testid={`edit-asset-${asset.id}`}
                        >
                          <Edit2 className="w-4 h-4 text-green-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(asset.id)}
                          className="btn-icon delete"
                          title="Delete"
                          data-testid={`delete-asset-${asset.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-8 text-gray-500">
                    No assets found. Click "New Asset" to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Asset Details Panel */}
      {selectedAsset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold">Asset Details</h3>
                <button onClick={() => setSelectedAsset(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="col-span-2 bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-purple-600 rounded-xl flex items-center justify-center text-white">
                      {getAssetIcon(selectedAsset.asset_type)}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{selectedAsset.asset_number}</p>
                      <h4 className="text-xl font-bold">{selectedAsset.name_english}</h4>
                      <p className="text-gray-600">{selectedAsset.name_arabic}</p>
                    </div>
                  </div>
                </div>

                {/* Financial Info */}
                <div className="bg-blue-50 p-4 rounded-xl">
                  <p className="text-sm text-blue-600 mb-1">Purchase Value</p>
                  <p className="text-2xl font-bold text-blue-700">{selectedAsset.purchase_value?.toLocaleString()} SAR</p>
                </div>

                <div className="bg-green-50 p-4 rounded-xl">
                  <p className="text-sm text-green-600 mb-1">Book Value</p>
                  <p className="text-2xl font-bold text-green-700">{selectedAsset.book_value?.toLocaleString()} SAR</p>
                </div>

                {selectedAsset.category === 'fixed' && (
                  <>
                    <div className="bg-red-50 p-4 rounded-xl">
                      <p className="text-sm text-red-600 mb-1">Annual Depreciation</p>
                      <p className="text-xl font-bold text-red-700">{selectedAsset.annual_depreciation?.toLocaleString()} SAR</p>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-xl">
                      <p className="text-sm text-orange-600 mb-1">Accumulated Depreciation</p>
                      <p className="text-xl font-bold text-orange-700">{selectedAsset.accumulated_depreciation?.toLocaleString()} SAR</p>
                    </div>
                  </>
                )}

                {/* Details */}
                <div className="col-span-2 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Purchase Date</p>
                    <p className="font-semibold">{selectedAsset.purchase_date}</p>
                  </div>
                  {selectedAsset.useful_life_years && (
                    <div>
                      <p className="text-sm text-gray-500">Useful Life</p>
                      <p className="font-semibold">{selectedAsset.useful_life_years} years</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[selectedAsset.status]}`}>
                      {statusLabels[selectedAsset.status]}
                    </span>
                  </div>
                  {selectedAsset.location && (
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="font-semibold">{selectedAsset.location}</p>
                    </div>
                  )}
                  {selectedAsset.project_name && (
                    <div>
                      <p className="text-sm text-gray-500">Project</p>
                      <p className="font-semibold">{selectedAsset.project_name}</p>
                    </div>
                  )}
                  {selectedAsset.employee_name && (
                    <div>
                      <p className="text-sm text-gray-500">Assigned To</p>
                      <p className="font-semibold">{selectedAsset.employee_name}</p>
                    </div>
                  )}
                  {selectedAsset.serial_number && (
                    <div>
                      <p className="text-sm text-gray-500">Serial Number</p>
                      <p className="font-semibold font-mono">{selectedAsset.serial_number}</p>
                    </div>
                  )}
                  {selectedAsset.warranty_expiry && (
                    <div>
                      <p className="text-sm text-gray-500">Warranty Expiry</p>
                      <p className="font-semibold">{selectedAsset.warranty_expiry}</p>
                    </div>
                  )}
                </div>

                {/* Attachment */}
                <div className="col-span-2">
                  <p className="text-sm text-gray-500 mb-2">Attachment</p>
                  {selectedAsset.attachment_url ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api${selectedAsset.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:underline"
                    >
                      <FileText className="w-5 h-5" />
                      View Attachment
                    </a>
                  ) : (
                    <label className="flex items-center gap-2 cursor-pointer text-gray-500 hover:text-purple-600">
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e, selectedAsset.id)}
                        disabled={uploadingFile}
                      />
                      <Upload className="w-5 h-5" />
                      {uploadingFile ? 'Uploading...' : 'Upload Document'}
                    </label>
                  )}
                </div>

                {selectedAsset.notes && (
                  <div className="col-span-2 bg-yellow-50 p-4 rounded-xl">
                    <p className="text-sm text-yellow-600 mb-1">Notes</p>
                    <p className="text-gray-700">{selectedAsset.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {editingAsset ? 'Edit Asset' : 'New Asset'}
              </h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Category */}
                <div className="col-span-2">
                  <label className="form-label">Category / التصنيف</label>
                  <div className="flex gap-4">
                    <label className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.category === 'fixed' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                    }`}>
                      <input
                        type="radio"
                        name="category"
                        value="fixed"
                        checked={formData.category === 'fixed'}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <Building2 className={`w-8 h-8 mx-auto mb-2 ${formData.category === 'fixed' ? 'text-blue-600' : 'text-gray-400'}`} />
                        <p className="font-semibold">Fixed Assets</p>
                        <p className="text-xs text-gray-500">أصول ثابتة</p>
                      </div>
                    </label>
                    <label className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.category === 'current' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                    }`}>
                      <input
                        type="radio"
                        name="category"
                        value="current"
                        checked={formData.category === 'current'}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <DollarSign className={`w-8 h-8 mx-auto mb-2 ${formData.category === 'current' ? 'text-green-600' : 'text-gray-400'}`} />
                        <p className="font-semibold">Current Assets</p>
                        <p className="text-xs text-gray-500">أصول متداولة</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Asset Type */}
                <div className="col-span-2">
                  <label className="form-label">Asset Type / نوع الأصل</label>
                  <select
                    value={formData.asset_type}
                    onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })}
                    className="form-input"
                    required
                  >
                    {formData.category === 'fixed' ? (
                      <>
                        <option value="building">Building / مبنى</option>
                        <option value="land">Land / أرض</option>
                        <option value="vehicle">Vehicle / مركبة</option>
                        <option value="equipment">Equipment / معدات</option>
                        <option value="furniture">Furniture / أثاث</option>
                        <option value="computer">Computer / حاسوب</option>
                        <option value="machinery">Machinery / آلات</option>
                      </>
                    ) : (
                      <>
                        <option value="cash">Cash / نقدية</option>
                        <option value="inventory">Inventory / مخزون</option>
                        <option value="receivables">Receivables / ذمم مدينة</option>
                        <option value="prepaid">Prepaid / مصاريف مقدمة</option>
                        <option value="other">Other / أخرى</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Names */}
                <div>
                  <label className="form-label">Name (English)</label>
                  <input
                    type="text"
                    value={formData.name_english}
                    onChange={(e) => setFormData({ ...formData, name_english: e.target.value })}
                    className="form-input"
                    required
                    data-testid="asset-name-english"
                  />
                </div>
                <div>
                  <label className="form-label">الاسم (عربي)</label>
                  <input
                    type="text"
                    value={formData.name_arabic}
                    onChange={(e) => setFormData({ ...formData, name_arabic: e.target.value })}
                    className="form-input"
                    dir="rtl"
                    required
                    data-testid="asset-name-arabic"
                  />
                </div>

                {/* Purchase Info */}
                <div>
                  <label className="form-label">Purchase Date / تاريخ الشراء</label>
                  <input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Purchase Value (SAR) / قيمة الشراء</label>
                  <input
                    type="number"
                    value={formData.purchase_value}
                    onChange={(e) => setFormData({ ...formData, purchase_value: e.target.value })}
                    className="form-input"
                    required
                    min="0"
                    step="0.01"
                    data-testid="asset-purchase-value"
                  />
                </div>

                {/* Depreciation (Fixed Assets Only) */}
                {formData.category === 'fixed' && (
                  <>
                    <div>
                      <label className="form-label">Useful Life (Years) / العمر الافتراضي</label>
                      <input
                        type="number"
                        value={formData.useful_life_years}
                        onChange={(e) => setFormData({ ...formData, useful_life_years: e.target.value })}
                        className="form-input"
                        min="1"
                        max="100"
                      />
                    </div>
                    <div>
                      <label className="form-label">Salvage Value (SAR) / القيمة التخريدية</label>
                      <input
                        type="number"
                        value={formData.salvage_value}
                        onChange={(e) => setFormData({ ...formData, salvage_value: e.target.value })}
                        className="form-input"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </>
                )}

                {/* Location & Assignment */}
                <div>
                  <label className="form-label">Location / الموقع</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Project / المشروع</label>
                  <select
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    className="form-input"
                  >
                    <option value="">Select Project</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Assigned To / مسؤول عنه</label>
                  <select
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    className="form-input"
                  >
                    <option value="">Select Employee</option>
                    {employees.map(e => (
                      <option key={e.id} value={e.id}>{e.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Serial Number / الرقم التسلسلي</label>
                  <input
                    type="text"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="col-span-2">
                  <label className="form-label">Warranty Expiry / انتهاء الضمان</label>
                  <input
                    type="date"
                    value={formData.warranty_expiry}
                    onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="col-span-2">
                  <label className="form-label">Description / الوصف</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="form-input"
                    rows="2"
                  />
                </div>

                <div className="col-span-2">
                  <label className="form-label">Notes / ملاحظات</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="form-input"
                    rows="2"
                  />
                </div>
              </div>

              {/* Phase 7.x · Fix #5 · Batch 1 — Purchase Journal section.
                  Every new asset MUST emit a balanced journal entry:
                    DR linked_account_id  /  CR <source account>
                  Source comes from the Payment Source picker below. */}
              {!editingAsset && (
              <div className="border-2 border-dashed border-indigo-200 rounded-xl p-4 bg-indigo-50/30 space-y-4">
                <div className="flex items-center gap-2 text-indigo-700 font-semibold">
                  <BookOpen className="w-4 h-4" />
                  <span>Purchase Journal · القيد المحاسبي للشراء</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="form-label">Asset GL Account * (حساب الأصل · DR)</label>
                    <select
                      data-testid="asset-linked-account-select"
                      value={formData.linked_account_id}
                      onChange={(e) => setFormData({ ...formData, linked_account_id: e.target.value })}
                      className="form-input"
                      required
                    >
                      <option value="">— Select asset account —</option>
                      {accounts
                        .filter(a => a.account_type === 'asset' && a.status !== 'inactive')
                        .map(a => (
                        <option key={a.id} value={a.id}>
                          {a.account_code ? `${a.account_code} · ` : ''}{a.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="form-label">Payment Source *</label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { v: 'payable',    label: 'Payable',    icon: CreditCard, color: 'amber' },
                        { v: 'bank',       label: 'Bank',       icon: Building2,  color: 'blue' },
                        { v: 'petty_cash', label: 'Petty Cash', icon: Wallet,     color: 'orange' },
                        { v: 'cash',       label: 'Cash',       icon: DollarSign, color: 'green' },
                      ].map(({ v, label, icon: Icon, color }) => (
                        <button
                          type="button"
                          key={v}
                          data-testid={`asset-payment-${v}-btn`}
                          onClick={() => setFormData({
                            ...formData, payment_source: v,
                            payable_account_id: '', petty_cash_fund_id: '', bank_account_id: '', cash_account_id: ''
                          })}
                          className={`flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-lg border-2 transition-all text-sm ${
                            formData.payment_source === v
                              ? `border-${color}-500 bg-${color}-50 text-${color}-700 font-semibold`
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {formData.payment_source === 'payable' && (
                    <div className="col-span-2">
                      <label className="form-label">Payable Account * (حساب الموردين · CR)</label>
                      <select
                        data-testid="asset-payable-select"
                        value={formData.payable_account_id}
                        onChange={(e) => setFormData({ ...formData, payable_account_id: e.target.value })}
                        className="form-input"
                        required
                      >
                        <option value="">— Select payable —</option>
                        {accounts.filter(a => a.account_type === 'liability' && a.status !== 'inactive').map(a => (
                          <option key={a.id} value={a.id}>
                            {a.account_code ? `${a.account_code} · ` : ''}{a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {formData.payment_source === 'bank' && (
                    <div className="col-span-2">
                      <label className="form-label">Bank Account *</label>
                      <select
                        data-testid="asset-bank-select"
                        value={formData.bank_account_id}
                        onChange={(e) => setFormData({ ...formData, bank_account_id: e.target.value })}
                        className="form-input"
                        required
                      >
                        <option value="">— Select bank —</option>
                        {bankAccounts.map(b => (
                          <option key={b.id} value={b.id}>
                            {b.account_name} · {(b.current_balance || 0).toLocaleString()} SAR
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {formData.payment_source === 'petty_cash' && (
                    <div className="col-span-2">
                      <label className="form-label">Petty Cash Fund *</label>
                      <select
                        data-testid="asset-petty-cash-select"
                        value={formData.petty_cash_fund_id}
                        onChange={(e) => setFormData({ ...formData, petty_cash_fund_id: e.target.value })}
                        className="form-input"
                        required
                      >
                        <option value="">— Select fund —</option>
                        {pettyFunds.map(f => (
                          <option key={f.id} value={f.id}>
                            {f.name} · Balance: {(f.current_balance || 0).toLocaleString()} SAR
                            {f.employee_name ? ` · ${f.employee_name}` : ''}
                          </option>
                        ))}
                      </select>
                      {pcInsufficient && (
                        <p data-testid="asset-pc-insufficient" className="text-rose-600 text-xs mt-1 font-medium">
                          ⚠ الرصيد غير كافٍ — المطلوب {purchaseTotal.toLocaleString()} SAR.
                        </p>
                      )}
                    </div>
                  )}

                  {formData.payment_source === 'cash' && (
                    <div className="col-span-2">
                      <label className="form-label">Cash Account * (حساب النقدية · CR)</label>
                      <select
                        data-testid="asset-cash-select"
                        value={formData.cash_account_id}
                        onChange={(e) => setFormData({ ...formData, cash_account_id: e.target.value })}
                        className="form-input"
                        required
                      >
                        <option value="">— Select cash account —</option>
                        {accounts.filter(a => a.account_type === 'asset' && a.status !== 'inactive').map(a => (
                          <option key={a.id} value={a.id}>
                            {a.account_code ? `${a.account_code} · ` : ''}{a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Live journal preview */}
                {formData.linked_account_id && purchaseTotal > 0 && (
                  <div data-testid="journal-preview" className="text-xs bg-white border border-indigo-100 rounded-lg p-2 font-mono">
                    <div className="text-emerald-700">
                      DR  {accounts.find(a => a.id === formData.linked_account_id)?.name || '?'}
                      <span className="float-right">{purchaseTotal.toLocaleString()} SAR</span>
                    </div>
                    <div className="text-rose-700">
                      CR  {(formData.payment_source === 'payable' && accounts.find(a => a.id === formData.payable_account_id)?.name) ||
                           (formData.payment_source === 'bank' && bankAccounts.find(b => b.id === formData.bank_account_id)?.account_name) ||
                           (formData.payment_source === 'petty_cash' && pettyFunds.find(f => f.id === formData.petty_cash_fund_id)?.name) ||
                           (formData.payment_source === 'cash' && accounts.find(a => a.id === formData.cash_account_id)?.name) ||
                           '?'}
                      <span className="float-right">{purchaseTotal.toLocaleString()} SAR</span>
                    </div>
                  </div>
                )}
              </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={pcInsufficient}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-asset-btn"
                >
                  {editingAsset ? 'Update Asset' : 'Create Asset'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assets;
